import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Activity, Flame, Star, RefreshCw, ChevronDown, ChevronUp,
  Hand, Flag, Users, Trophy, Clock, Search, X, PauseCircle, Bell, Send, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";
import { detectCategory, groupMatchesByCategory } from "@/utils/categories";
import Modal from "@/components/Modal";
import { Spinner } from "@/components/Spinner";

const CAT_ICON = { sports: Activity, flag: Flag, people: Users, trophy: Trophy };
const fmtOdds = (o) => (o ? Number(o).toFixed(2) : "-");
const fmtProb = (p) => (p ? `${(p * 100).toFixed(0)}%` : "-");
const formatAgo = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
};

// Match a live-score match to a Cloudbet market by player surnames (order-independent),
// then attach each player's current odds so the Live Scores tab can show live odds.
const surname = (n) => (n || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase().replace(/[.,]/g, "").split(/\s+/).pop();
const findCloudbetMarket = (m, cloudbet) => {
  const a1 = surname(m.player1), a2 = surname(m.player2);
  if (!a1 || !a2) return null;
  return cloudbet.find((cb) => {
    const b1 = surname(cb.player1), b2 = surname(cb.player2);
    return (a1 === b1 && a2 === b2) || (a1 === b2 && a2 === b1);
  }) || null;
};
const attachOdds = (liveMatches, cloudbet) => liveMatches.map((m) => {
  const cb = findCloudbetMarket(m, cloudbet);
  if (!cb) return m;
  const p1IsHome = surname(m.player1) === surname(cb.player1);
  return { ...m, _odds1: p1IsHome ? cb.home_odds : cb.away_odds, _odds2: p1IsHome ? cb.away_odds : cb.home_odds, _cb: cb };
});
// True when a live-score match and a Cloudbet event are the same fixture (surname, order-independent)
const sameMatch = (m, cb) => {
  const a1 = surname(m.player1), a2 = surname(m.player2);
  const b1 = surname(cb.player1), b2 = surname(cb.player2);
  return !!a1 && !!a2 && ((a1 === b1 && a2 === b2) || (a1 === b2 && a2 === b1));
};
// Normalise a Cloudbet live event into the unified live-card shape (used for live
// matches that the RapidAPI scores feed does not return, so none are missed).
const fromCloudbet = (cb) => ({
  match_id: cb.event_id,
  player1: cb.player1,
  player2: cb.player2,
  tournament: cb.tournament,
  score: cb.score && cb.score !== "No live score" ? cb.score : "",
  match_point_detected: false,
  match_point_player: null,
  _odds1: cb.home_odds,
  _odds2: cb.away_odds,
  _cb: cb,
});
const FAV_KEY = "@breakpoint_fav_matches";
// Stable favourite key for a fixture (order-independent surnames), so a starred match
// stays starred across RapidAPI/Cloudbet sources and player ordering.
const favKey = (m) => [surname(m.player1), surname(m.player2)].sort().join("|");
const isInterrupted = (s) => /interrupt|suspend|delay|rain/i.test(s || "");
// Live phase chip from api-tennis event_status ("Set 2" -> "SET 2", tie-break, etc.)
const phaseLabel = (s) => {
  if (!s) return null;
  const m = /set\s*(\d)/i.exec(s);
  if (m) return `SET ${m[1]}`;
  if (/tie.?break/i.test(s)) return "TIE-BREAK";
  return null;
};
// A real, meaningful score (not the placeholder "0-0" we fall back to when the live-score
// provider hasn't returned set data yet).
const hasRealScore = (s) => !!s && s !== "0-0" && s !== "No live score";
const ScoreLine = ({ score }) => (
  <div className="flex items-center bg-[#0f172a] rounded-lg px-2.5 py-2 mt-1" data-testid="match-score-line">
    <span className="text-[13px] text-[#9ca3af] mr-2">Score:</span>
    {hasRealScore(score)
      ? <span className="text-[16px] text-[#10b981] font-bold">{score}</span>
      : <span className="text-[12px] text-[#f59e0b] font-semibold">Live · score updating…</span>}
  </div>
);

const StarToggle = ({ m, isFav, toggleFav }) => {
  const fav = isFav(m);
  return (
    <button
      onClick={(e) => { e.stopPropagation(); toggleFav(m); }}
      data-testid="fav-toggle"
      aria-label={fav ? "Unstar match" : "Star match"}
      className="p-0.5 -m-0.5 shrink-0"
    >
      <Star size={16} className={fav ? "text-[#f59e0b]" : "text-[#475569] hover:text-[#94a3b8]"} fill={fav ? "#f59e0b" : "none"} />
    </button>
  );
};

const CatHeader = ({ cat, count, k, collapsed, toggleCat }) => {
  const Icon = CAT_ICON[cat.icon] || Activity;
  const isCollapsed = collapsed[k] ?? false;
  return (
    <button onClick={() => toggleCat(k)} className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl mb-1" style={{ backgroundColor: cat.bgColor, borderLeft: `4px solid ${cat.color}` }}>
      <span className="flex items-center gap-2">
        <Icon size={18} color={cat.color} />
        <span className="text-sm font-extrabold tracking-wide" style={{ color: cat.color }}>{cat.label}</span>
        <span className="text-[11px] font-bold text-white rounded-full px-2 py-0.5" style={{ backgroundColor: cat.color }}>{count}</span>
      </span>
      {isCollapsed ? <ChevronDown size={20} color={cat.color} /> : <ChevronUp size={20} color={cat.color} />}
    </button>
  );
};

// Fixed tour columns for the main grid (live in-play AND pre-match use the SAME layout):
// 1) ATP (singles + doubles)  2) WTA (singles + doubles)  3) Challenger (singles + doubles).
// Any other category (Team Cups, Other) renders in a full-width row below the 3 columns.
const TOUR_COLUMNS = [
  { key: "atp", label: "ATP", color: "#3b82f6", cats: ["atp_singles", "atp_doubles"] },
  { key: "wta", label: "WTA", color: "#ec4899", cats: ["wta_singles", "wta_doubles"] },
  { key: "challenger", label: "Challenger", color: "#f59e0b", cats: ["challenger"] },
];
const TOUR_COLUMN_CATS = new Set(TOUR_COLUMNS.flatMap((c) => c.cats));


const LiveCard = ({ m, cat, onSelect, isFav, toggleFav }) => {
  const cb = m._cb;
  const bettable = cb && cb.selections_enabled && cb.home_odds;
  return (
    <div
      onClick={() => { if (bettable) onSelect(cb); }}
      className={`bg-[#1e293b] border border-[#334155] rounded-xl p-3 mb-2 ${bettable ? "cursor-pointer hover:border-[#10b981]/50" : ""}`}
      style={{ borderLeft: `3px solid ${cat.color}` }}
      data-testid="live-match-card"
    >
      <div className="flex justify-between items-center mb-1.5">
        {m.match_point_detected ? (
          <span className="inline-flex items-center gap-1 bg-[#dc2626] px-2 py-0.5 rounded text-white text-[11px] font-bold"><Star size={11} fill="#fff" /> MATCH POINT</span>
        ) : isInterrupted(m.status) ? (
          <span className="inline-flex items-center gap-1 bg-[#f59e0b]/15 px-2 py-0.5 rounded text-[#f59e0b] text-[11px] font-bold" data-testid="interrupted-badge"><PauseCircle size={12} /> INTERRUPTED</span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 bg-[#dc2626]/15 px-2 py-0.5 rounded text-[#dc2626] text-[11px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#dc2626] bp-pulse" /> LIVE
            </span>
            {phaseLabel(m.status) && <span className="bg-[#3b82f6]/15 text-[#3b82f6] px-1.5 py-0.5 rounded text-[10px] font-bold" data-testid="match-phase-badge">{phaseLabel(m.status)}</span>}
          </span>
        )}
        <StarToggle m={m} isFav={isFav} toggleFav={toggleFav} />
      </div>
      <div className="mb-2">
        <div className="flex justify-between items-center py-0.5">
          <span className={`text-[15px] mr-2 ${m.match_point_player === m.player1 ? "text-[#10b981] font-bold" : "text-white font-medium"}`}>{m.player1}</span>
          {m._odds1 ? <span className="px-2.5 py-1 rounded-md bg-[#0f172a] text-[#10b981] font-bold text-[14px] shrink-0" data-testid="live-odds">{fmtOdds(m._odds1)}</span> : null}
        </div>
        <div className="flex justify-between items-center py-0.5">
          <span className={`text-[15px] mr-2 ${m.match_point_player === m.player2 ? "text-[#10b981] font-bold" : "text-white font-medium"}`}>{m.player2}</span>
          {m._odds2 ? <span className="px-2.5 py-1 rounded-md bg-[#0f172a] text-[#10b981] font-bold text-[14px] shrink-0" data-testid="live-odds">{fmtOdds(m._odds2)}</span> : null}
        </div>
      </div>
      <ScoreLine score={m.score} />
      {bettable && <div className="flex items-center justify-center gap-1 mt-2 text-[#10b981] text-[12px]"><Hand size={14} /> Tap to bet</div>}
    </div>
  );
};

const CloudbetCard = ({ ev, cat, isLive, onSelect, isFav, toggleFav }) => {
  const clickable = ev.selections_enabled && ev.home_odds;
  return (
    <div
      onClick={() => { if (clickable) onSelect(ev); }}
      className={`bg-[#1e293b] border border-[#334155] rounded-xl p-3 mb-2 ${clickable ? "cursor-pointer hover:border-[#10b981]/50" : ""}`}
      style={{ borderLeft: `3px solid ${cat.color}` }}
      data-testid="cloudbet-match-card"
    >
      <div className="flex justify-between items-center mb-2.5 gap-2">
        <span className="text-[12px] font-semibold truncate" style={{ color: cat.color }}>{ev.tournament}</span>
        <div className="flex items-center gap-1.5 shrink-0">
          {isLive && <span className="inline-flex items-center gap-1 bg-[#dc2626]/15 px-2 py-0.5 rounded text-[#dc2626] text-[11px] font-bold"><span className="w-1.5 h-1.5 rounded-full bg-[#dc2626] bp-pulse" /> LIVE</span>}
          <StarToggle m={ev} isFav={isFav} toggleFav={toggleFav} />
        </div>
      </div>
      {[{ name: ev.player1, odds: ev.home_odds }, { name: ev.player2, odds: ev.away_odds }].map((p, i) => (
        <div key={p.name || i} className="flex justify-between items-center py-1.5">
          <span className="text-[15px] text-white font-medium truncate mr-2">{p.name}</span>
          <span className={`px-3 py-1.5 rounded-md min-w-[60px] text-center font-bold text-[15px] text-[#10b981] ${p.odds && p.odds < 2 ? "bg-[#10b981]/15" : "bg-[#0f172a]"}`}>{fmtOdds(p.odds)}</span>
        </div>
      ))}
      {isLive ? (
        <ScoreLine score={ev.score} />
      ) : (ev.score && ev.score !== "No live score" && (
        <div className="flex items-center bg-[#0f172a] rounded-lg px-2.5 py-2 mt-1"><span className="text-[13px] text-[#9ca3af] mr-2">Score:</span><span className="text-[15px] text-[#10b981] font-bold">{ev.score}</span></div>
      ))}
      {clickable && <div className="flex items-center justify-center gap-1 mt-2 text-[#10b981] text-[12px]"><Hand size={14} /> Tap to bet</div>}
    </div>
  );
};

// Module-level cache (survives tab unmount/remount) → live matches render instantly on
// return while a silent background refresh runs. Spinner shows only on the first load.
const _mCache = { matches: [], cloudbet: [], stats: null, alerts: [], lastUpdated: null, ready: false };

export default function Matches() {
  const [matches, setMatches] = useState(() => _mCache.matches);
  const [cloudbet, setCloudbet] = useState(() => _mCache.cloudbet);
  const [stats, setStats] = useState(() => _mCache.stats);
  const [alerts, setAlerts] = useState(() => _mCache.alerts);
  const [showAlerts, setShowAlerts] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  // bet modal
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showBet, setShowBet] = useState(false);
  const [betStake, setBetStake] = useState("5");
  const [betPlayer, setBetPlayer] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(() => _mCache.lastUpdated);
  const [loaded, setLoaded] = useState(() => _mCache.ready);
  const [now, setNow] = useState(() => Date.now());
  const [query, setQuery] = useState("");
  const [favs, setFavs] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || "[]")); } catch { return new Set(); }
  });
  const isFav = (m) => favs.has(favKey(m));
  const toggleFav = (m) => setFavs((prev) => {
    const next = new Set(prev);
    const k = favKey(m);
    if (next.has(k)) next.delete(k); else next.add(k);
    localStorage.setItem(FAV_KEY, JSON.stringify([...next]));
    return next;
  });

  const fetchMatches = useCallback(async () => {
    const d = await apiGet("/api/matches/live"); if (d) { const m = d.matches || []; setMatches(m); _mCache.matches = m; const t = Date.now(); setLastUpdated(t); _mCache.lastUpdated = t; _mCache.ready = true; setLoaded(true); }
  }, []);
  const fetchCloudbet = useCallback(async () => {
    const d = await apiGet("/api/matches/all-cloudbet"); if (d) { const m = d.matches || []; setCloudbet(m); _mCache.cloudbet = m; _mCache.ready = true; setLoaded(true); }
  }, []);
  const fetchStats = useCallback(async () => {
    const d = await apiGet("/api/stats"); if (d) { setStats(d); _mCache.stats = d; }
  }, []);
  const fetchAlerts = useCallback(async () => {
    const d = await apiGet("/api/telegram/alerts"); if (d) { const a = d.alerts || []; setAlerts(a); _mCache.alerts = a; }
  }, []);

  useEffect(() => {
    Promise.all([fetchMatches(), fetchCloudbet(), fetchStats(), fetchAlerts()]);
    const i = setInterval(() => { fetchMatches(); fetchCloudbet(); fetchAlerts(); }, 30000);
    return () => clearInterval(i);
  }, [fetchMatches, fetchCloudbet, fetchStats, fetchAlerts]);

  // Browser WebSocket — instant point-by-point score/status updates from the api-tennis feed.
  // Updates existing live cards in place (matched by surname) and removes finished matches the
  // moment the feed reports them, without waiting for the 30s poll. The poll stays as a fallback.
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    if (!base) return;
    const wsUrl = base.replace(/^http/, "ws") + "/api/ws/live";
    let ws, closed = false, reconnectTimer;
    const applyEvents = (events) => {
      if (!events || !events.length) return;
      setMatches((prev) => {
        let changed = false;
        const next = prev
          .map((m) => {
            const ev = events.find((e) => sameMatch(m, { player1: e.p1, player2: e.p2 }));
            if (!ev) return m;
            if (ev.finished) { changed = true; return { ...m, _finished: true }; }
            if ((ev.score || m.score) === m.score && (ev.status || m.status) === m.status) return m;
            changed = true;
            return { ...m, score: ev.score || m.score, status: ev.status || m.status };
          })
          .filter((m) => !m._finished);
        return changed ? next : prev;
      });
      setLastUpdated(Date.now());
    };
    const connect = () => {
      try { ws = new WebSocket(wsUrl); } catch { return; }
      ws.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data);
          if (d.type === "snapshot") applyEvents(d.events);
          else if (d.type === "score") applyEvents([d]);
        } catch { /* ignore malformed frames */ }
      };
      ws.onclose = () => { if (!closed) reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    return () => { closed = true; clearTimeout(reconnectTimer); try { ws && ws.close(); } catch { /* noop */ } };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchMatches(), fetchCloudbet(), fetchStats()]);
    setRefreshing(false);
  };

  const toggleCat = (k) => setCollapsed((p) => ({ ...p, [k]: !p[k] }));

  const onSelectEvent = (ev) => { setSelectedEvent(ev); setBetPlayer(null); setBetStake("5"); setShowBet(true); };

  const placeBet = async () => {
    if (!selectedEvent || !betPlayer) return;
    setPlacing(true);
    try {
      const odds = betPlayer === "home" ? selectedEvent.home_odds : selectedEvent.away_odds;
      const data = await apiPost("/api/cloudbet/wallet/place-bet", {
        event_id: selectedEvent.event_id,
        market_url: `tennis.winner/${betPlayer}`,
        odds, stake: parseFloat(betStake), accept_price_change: "BETTER",
      });
      const playerName = betPlayer === "home" ? selectedEvent.player1 : selectedEvent.player2;
      if (!data) { toast.error("Request timed out — your bet may have been placed. Check Positions / History before retrying.", { duration: 7000 }); setShowBet(false); return; }
      if (data.success) {
        const status = data.status || "UNKNOWN";
        if (status === "ACCEPTED") toast.success(`Bet accepted: ${playerName} @ ${data.odds || odds}x for $${betStake} USDT`);
        else if (status === "PENDING_ACCEPTANCE") toast(`Bet pending confirmation for ${playerName}. Check positions.`);
        else toast.success(`Bet placed: ${playerName} @ ${odds}x — ${status}`);
        setShowBet(false);
      } else if (data.cloudflare_blocked) {
        toast.error("Cloudbet's trading API blocks cloud server IPs. Bet placement works when running locally. All data/odds/balance endpoints work.");
      } else {
        toast.error(data.error || "Could not place bet");
      }
    } catch {
      toast.error("Network error placing bet");
    } finally { setPlacing(false); }
  };

  const enrichedMatches = attachOdds(matches, cloudbet);
  const liveCloudbet = cloudbet.filter((m) => m.live || m.has_live_data);
  // Live matches Cloudbet knows about but the RapidAPI scores feed never returned,
  // so the unified Live list never misses a live match.
  const usedCbIds = new Set(enrichedMatches.map((m) => m._cb && m._cb.event_id).filter(Boolean));
  const cloudbetOnlyLive = liveCloudbet.filter(
    (c) => !usedCbIds.has(c.event_id) && !enrichedMatches.some((m) => sameMatch(m, c))
  );
  const unifiedLive = [...enrichedMatches, ...cloudbetOnlyLive.map(fromCloudbet)];
  // Search/filter by player name or tournament across the whole unified page.
  const q = query.trim().toLowerCase();
  const matchesQuery = (m) => !q || [m.player1, m.player2, m.tournament].some((x) => (x || "").toLowerCase().includes(q));
  const unifiedLiveShown = unifiedLive.filter(matchesQuery);
  const preCloudbetAll = cloudbet.filter((m) => !m.live && !m.has_live_data);
  const preCloudbetShown = preCloudbetAll.filter(matchesQuery);
  // Starred matches float to a Favorites section at the very top; remove them from the
  // sections below to avoid showing the same match twice.
  const favLive = unifiedLiveShown.filter(isFav);
  const favPre = preCloudbetShown.filter(isFav);
  const matchPointMatches = unifiedLiveShown.filter((m) => m.match_point_detected && !isFav(m));
  const regular = unifiedLiveShown.filter((m) => !m.match_point_detected && !isFav(m));
  const liveGrouped = groupMatchesByCategory(regular);
  const preCloudbet = preCloudbetShown.filter((m) => !isFav(m));
  const preCloudGrouped = groupMatchesByCategory(preCloudbet);
  const noSearchResults = !!q && favLive.length === 0 && favPre.length === 0 && matchPointMatches.length === 0 && regular.length === 0 && preCloudbet.length === 0;

  const renderGroup = (group, Card, isLive) => {
    const k = `${isLive ? "live" : "pre"}_${group.category.key}`;
    const isCollapsed = collapsed[k] ?? false;
    return (
      <div key={k} className="mb-4 break-inside-avoid">
        <CatHeader cat={group.category} count={group.totalMatches} k={k} collapsed={collapsed} toggleCat={toggleCat} />
        {!isCollapsed && group.tournaments.map((tg) => (
          <div key={tg.tournament} className="mb-1">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: group.category.color }} />
              <span className="flex-1 text-[12px] text-[#9ca3af] font-semibold truncate">{tg.tournament}</span>
              <span className="text-[12px] font-bold" style={{ color: group.category.color }}>{tg.matches.length}</span>
            </div>
            {tg.matches.map((m, idx) => (
              <Card key={m.match_id || m.event_id || idx} m={m} ev={m} cat={group.category} isLive={isLive} onSelect={onSelectEvent} isFav={isFav} toggleFav={toggleFav} />
            ))}
          </div>
        ))}
      </div>
    );
  };

  // Render the main match grid as 3 fixed tour columns (ATP | WTA | Challenger), each stacking
  // that tour's singles + doubles groups. Leftover categories (Team Cups, Other) render full-width
  // below. Used IDENTICALLY for live in-play and pre-match so both displays match.
  const renderTourColumns = (grouped, Card, isLive, emptyLabel) => {
    const byKey = {};
    for (const g of grouped) byKey[g.category.key] = g;
    const leftover = grouped.filter((g) => !TOUR_COLUMN_CATS.has(g.category.key));
    return (
      <>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid={`${isLive ? "live" : "pre"}-tour-grid`}>
          {TOUR_COLUMNS.map((col) => {
            const groups = col.cats.map((c) => byKey[c]).filter(Boolean);
            const total = groups.reduce((s, g) => s + g.totalMatches, 0);
            return (
              <div key={col.key} data-testid={`${isLive ? "live" : "pre"}-col-${col.key}`} className={total === 0 ? "hidden lg:block" : ""}>
                <div className="flex items-center gap-2 px-2.5 py-2 mb-2 rounded-lg" style={{ backgroundColor: `${col.color}14`, borderBottom: `2px solid ${col.color}` }}>
                  <span className="text-[13px] font-extrabold tracking-wide" style={{ color: col.color }}>{col.label}</span>
                  <span className="text-[11px] font-bold text-white rounded-full px-2 py-0.5" style={{ backgroundColor: col.color }}>{total}</span>
                </div>
                {groups.length > 0
                  ? groups.map((g) => renderGroup(g, Card, isLive))
                  : <p className="text-[12px] text-[#6b7280] px-2.5 py-3">{emptyLabel}</p>}
              </div>
            );
          })}
        </div>
        {leftover.length > 0 && (
          <div className="columns-1 lg:columns-2 2xl:columns-3 gap-4 mt-4">
            {leftover.map((g) => renderGroup(g, Card, isLive))}
          </div>
        )}
      </>
    );
  };


  return (
    <div className="p-4 md:p-6 max-w-screen-2xl mx-auto">
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white">Matches</h1>
          {lastUpdated && (
            <span data-testid="live-updated-indicator" className="flex items-center gap-1.5 text-[12px] text-[#9ca3af] bg-[#1e293b] border border-[#334155] px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10b981] bp-pulse" />
              Live · updated {formatAgo(now - lastUpdated)}
            </span>
          )}
        </div>
        <button onClick={onRefresh} data-testid="matches-refresh" className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white bg-[#1e293b] border border-[#334155] px-3 py-2 rounded-lg">
          <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Search / filter */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b7280]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="matches-search-input"
          placeholder="Search by player or tournament…"
          className="w-full bg-[#1e293b] border border-[#334155] rounded-xl py-3 pl-10 pr-10 text-[15px] text-white placeholder-[#6b7280] outline-none focus:border-[#10b981]"
        />
        {query && (
          <button onClick={() => setQuery("")} data-testid="matches-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b7280] hover:text-white">
            <X size={18} />
          </button>
        )}
      </div>

      {stats && (
        <div className="flex bg-[#1e293b] border border-[#334155] rounded-xl p-4 mb-3">
          <div className="flex-1 text-center"><p className="text-2xl font-bold text-[#10b981]">{unifiedLive.length}</p><p className="text-[11px] text-[#9ca3af] mt-0.5">Live</p></div>
          <div className="w-px bg-[#334155] mx-2" />
          <div className="flex-1 text-center"><p className="text-2xl font-bold text-[#3b82f6]">{preCloudbetAll.length}</p><p className="text-[11px] text-[#9ca3af] mt-0.5">Upcoming</p></div>
          <div className="w-px bg-[#334155] mx-2" />
          <button onClick={() => setShowAlerts(true)} data-testid="alerts-stat-btn" className="flex-1 text-center group">
            <p className="text-2xl font-bold text-[#10b981] flex items-center justify-center gap-1"><Bell size={16} className="text-[#f59e0b]" />{alerts.length}</p>
            <p className="text-[11px] text-[#9ca3af] mt-0.5 group-hover:text-white">Alerts</p>
          </button>
        </div>
      )}

          {(favLive.length > 0 || favPre.length > 0) && (
            <div className="mb-3" data-testid="favorites-section">
              <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl mb-1" style={{ backgroundColor: "#f59e0b20", borderLeft: "4px solid #f59e0b" }}>
                <Star size={18} color="#f59e0b" fill="#f59e0b" />
                <span className="text-sm font-extrabold tracking-wide text-[#f59e0b]">FAVORITES</span>
                <span className="text-[11px] font-bold text-white rounded-full px-2 py-0.5 bg-[#f59e0b]">{favLive.length + favPre.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3">
                {favLive.map((m) => (
                  <LiveCard key={`fav_${favKey(m)}`} m={m} cat={detectCategory(m.tournament, m.player1, m.player2)} onSelect={onSelectEvent} isFav={isFav} toggleFav={toggleFav} />
                ))}
                {favPre.map((m) => (
                  <CloudbetCard key={`fav_${favKey(m)}`} ev={m} cat={detectCategory(m.tournament, m.player1, m.player2)} isLive={false} onSelect={onSelectEvent} isFav={isFav} toggleFav={toggleFav} />
                ))}
              </div>
            </div>
          )}

          {matchPointMatches.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl mb-1" style={{ backgroundColor: "#ef444420", borderLeft: "4px solid #ef4444" }}>
                <Flame size={18} color="#ef4444" />
                <span className="text-sm font-extrabold tracking-wide text-[#ef4444]">MATCH POINT</span>
                <span className="text-[11px] font-bold text-white rounded-full px-2 py-0.5 bg-[#ef4444]">{matchPointMatches.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3">
              {matchPointMatches.map((m) => {
                const cat = detectCategory(m.tournament, m.player1, m.player2);
                const bettable = m._cb && m._cb.selections_enabled && m._cb.home_odds;
                return (
                  <div key={m.match_id} onClick={() => { if (bettable) onSelectEvent(m._cb); }} className={`bg-[#1e293b] border-2 border-[#ef4444] rounded-xl p-3 mb-2 ${bettable ? "cursor-pointer hover:border-[#10b981]" : ""}`}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="inline-flex items-center gap-1 bg-[#dc2626] px-2.5 py-1 rounded text-white text-[11px] font-bold"><Star size={12} /> MATCH POINT</span>
                      <StarToggle m={m} isFav={isFav} toggleFav={toggleFav} />
                    </div>
                    <p className="text-[12px] mb-1.5" style={{ color: cat.color }}>{m.tournament}</p>
                    <div className="flex justify-between items-center py-0.5">
                      <span className={`text-[15px] mr-2 ${m.match_point_player === m.player1 ? "text-[#10b981] font-bold" : "text-white"}`}>{m.player1} {m.match_point_player === m.player1 ? "🏆" : ""}</span>
                      {m._odds1 ? <span className="px-2.5 py-1 rounded-md bg-[#0f172a] text-[#10b981] font-bold text-[14px] shrink-0" data-testid="live-odds">{fmtOdds(m._odds1)}</span> : null}
                    </div>
                    <div className="flex justify-between items-center py-0.5">
                      <span className={`text-[15px] mr-2 ${m.match_point_player === m.player2 ? "text-[#10b981] font-bold" : "text-white"}`}>{m.player2} {m.match_point_player === m.player2 ? "🏆" : ""}</span>
                      {m._odds2 ? <span className="px-2.5 py-1 rounded-md bg-[#0f172a] text-[#10b981] font-bold text-[14px] shrink-0" data-testid="live-odds">{fmtOdds(m._odds2)}</span> : null}
                    </div>
                    {m.score ? <ScoreLine score={m.score} /> : null}
                    {bettable && <div className="flex items-center justify-center gap-1 mt-2 text-[#10b981] text-[12px]"><Hand size={14} /> Tap to bet</div>}
                  </div>
                );
              })}
              </div>
            </div>
          )}

          {regular.length > 0 && (
            <div className="flex items-center gap-2 mb-2 px-1"><span className="w-1.5 h-1.5 rounded-full bg-[#dc2626] bp-pulse" /><span className="text-base font-bold text-white">Live In-Play ({regular.length})</span></div>
          )}
          {regular.length > 0 && renderTourColumns(liveGrouped, LiveCard, true, "No live matches")}

          {preCloudbet.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2 px-1"><Clock size={18} color="#3b82f6" /><span className="text-base font-bold text-white">Pre-Match ({preCloudbet.length})</span></div>
              {renderTourColumns(preCloudGrouped, CloudbetCard, false, "No upcoming matches")}
            </div>
          )}

          {!loaded && cloudbet.length === 0 && matches.length === 0 && (
            <div className="flex flex-col items-center py-12"><Spinner size={36} /><p className="text-[#6b7280] text-base mt-3">Loading matches…</p></div>
          )}
          {noSearchResults && (
            <div className="flex flex-col items-center py-12" data-testid="matches-no-results">
              <Search size={56} color="#374151" />
              <p className="text-[#6b7280] text-base mt-3">No matches for “{query}”</p>
              <button onClick={() => setQuery("")} className="text-[#10b981] text-[13px] mt-2 hover:underline">Clear search</button>
            </div>
          )}
          {!q && (cloudbet.length > 0 || matches.length > 0) && unifiedLive.length === 0 && preCloudbetAll.length === 0 && (
            <div className="flex flex-col items-center py-12">
              <Activity size={56} color="#374151" />
              <p className="text-[#6b7280] text-base mt-3">No matches available right now</p>
            </div>
          )}

      {/* Recent Telegram alerts */}
      <Modal open={showAlerts} onClose={() => setShowAlerts(false)} title="Recent Telegram Alerts" testId="alerts-modal">
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {alerts.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-center" data-testid="alerts-empty">
              <Send size={40} color="#374151" />
              <p className="text-[#9ca3af] text-sm mt-3">No Telegram alerts yet.</p>
              <p className="text-[#6b7280] text-[12px] mt-1 max-w-xs">Trade executions, bets won and bets lost will appear here once your bot starts sending them. Connect your bot in Settings.</p>
            </div>
          ) : (
            alerts.map((a) => {
              const text = (a.message || "").replace(/<[^>]+>/g, "").trim();
              const title = text.split("\n")[0] || "Alert";
              const body = text.split("\n").slice(1).join("\n").trim();
              const when = a.sent_at ? new Date(a.sent_at).toLocaleString() : "";
              return (
                <div key={a.id} data-testid="alert-item" className="bg-[#0f172a] border border-[#334155] rounded-xl p-3">
                  <div className="flex items-start gap-2">
                    {a.success
                      ? <CheckCircle2 size={16} className="text-[#10b981] mt-0.5 shrink-0" />
                      : <AlertTriangle size={16} className="text-[#ef4444] mt-0.5 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] font-bold text-white">{title}</p>
                      {body && <p className="text-[12px] text-[#9ca3af] mt-1 whitespace-pre-line">{body}</p>}
                      <p className="text-[11px] text-[#6b7280] mt-1.5">{when}{!a.success ? " · delivery failed" : ""}</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Modal>

      {/* Bet modal */}
      <Modal open={showBet} onClose={() => setShowBet(false)} title="Place Bet on Cloudbet" testId="bet-modal">
        {selectedEvent && (
          <div>
            <p className="text-sm text-[#9ca3af] mb-4">{selectedEvent.tournament}</p>
            <p className="text-sm text-[#9ca3af] mb-2">Select Player</p>
            {[{ key: "home", name: selectedEvent.player1, odds: selectedEvent.home_odds, prob: selectedEvent.home_probability },
              { key: "away", name: selectedEvent.player2, odds: selectedEvent.away_odds, prob: selectedEvent.away_probability }].map((p) => (
              <button key={p.key} onClick={() => setBetPlayer(p.key)} data-testid={`bet-player-${p.key}`}
                className={`w-full flex justify-between items-center rounded-xl p-3.5 mb-2 border-2 ${betPlayer === p.key ? "border-[#10b981] bg-[#10b981]/10" : "border-transparent bg-[#0f172a]"}`}>
                <span className={`text-base font-semibold ${betPlayer === p.key ? "text-[#10b981]" : "text-white"}`}>{p.name}</span>
                <span className="text-right"><span className="block text-lg font-bold text-[#10b981]">{fmtOdds(p.odds)}</span><span className="block text-[12px] text-[#6b7280]">{fmtProb(p.prob)}</span></span>
              </button>
            ))}
            <p className="text-sm text-[#9ca3af] mb-2 mt-3">Stake (USDT)</p>
            <input value={betStake} onChange={(e) => setBetStake(e.target.value)} inputMode="decimal" data-testid="bet-stake-input"
              className="w-full bg-[#0f172a] border border-[#334155] rounded-xl p-4 text-white text-xl font-bold text-center outline-none focus:border-[#10b981] mb-2.5" />
            <div className="flex justify-between mb-4">
              {["1", "2", "5", "10"].map((a) => (
                <button key={a} onClick={() => setBetStake(a)} className="px-4 py-2 bg-[#334155] rounded-md text-white text-[13px] font-semibold">${a}</button>
              ))}
            </div>
            {betPlayer && (
              <div className="bg-[#10b981]/10 rounded-xl p-3.5 text-center mb-4">
                <p className="text-[12px] text-[#9ca3af]">Potential Winnings</p>
                <p className="text-2xl font-bold text-[#10b981] mt-1">${(parseFloat(betStake || "0") * ((betPlayer === "home" ? selectedEvent.home_odds : selectedEvent.away_odds) || 0)).toFixed(2)} USDT</p>
              </div>
            )}
            <button onClick={placeBet} disabled={!betPlayer || placing} data-testid="bet-place-btn"
              className="w-full bg-[#10b981] text-white font-bold py-4 rounded-xl disabled:opacity-50">
              {placing ? <Spinner size={20} color="#fff" /> : betPlayer ? `Bet $${betStake} on ${betPlayer === "home" ? selectedEvent.player1 : selectedEvent.player2}` : "Select a player"}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
